/**
 * StrategyEditorPanel
 *
 * Layout-agnostic strategy editor — the inline replacement for StrategyWizardDialog.
 * Reuses the same four hooks (form / positions / validation / submission) so all
 * validation + the frontend→backend transform + the create-vs-update API path are
 * identical to the dialog. Renders the aligned LegMatrix instead of stacked
 * position cards. Mounted inline on the basket page and inside a Modal on Schedule.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Info, ChevronDown, ChevronRight } from 'lucide-react';
import { isWhiteLabel } from '../../config/tenantConfig';
import Button from '../ui/Button';
import ConfirmDialog from '../common/ConfirmDialog';
import GlobalIndexSelector from './config/GlobalIndexSelector';
import StrategyConfiguration from './config/StrategyConfiguration';
import WizardErrorSummary from './header/WizardErrorSummary';
import LegMatrix from './leg-matrix/LegMatrix';
import { StrategyConfig, Leg } from '../../types/strategy';
import { computeStrategySignature, findDuplicateLegTypeGroups, legExposure, SELECTION_METHOD_OPTIONS } from '../../utils/strategy';
import { useToast } from '../common/ToastContainer';
import { useStrategyForm } from '../../hooks/strategy/useStrategyForm';
import { usePositionManagement } from '../../hooks/strategy/usePositionManagement';
import { useStrategyValidation } from '../../hooks/strategy/useStrategyValidation';
import { useStrategySubmission } from '../../hooks/strategy/useStrategySubmission';
import {
  TRADING_TYPE,
  INTRADAY_EXIT_MODE,
  EXPIRY_TYPE_CONST,
  ENTRY_SELECTION_MODE,
  MONTHLY_ONLY_UNDERLYINGS,
} from '../../constants/tradingConstants';

interface StrategyEditorPanelProps {
  basketId: string;
  editingStrategy?: any;
  onDone: (result: any) => void;
  onCancel: () => void;
  /** Reports unsaved-edit state to the host so it can guard accidental close. */
  onDirtyChange?: (dirty: boolean) => void;
}

const StrategyEditorPanel: React.FC<StrategyEditorPanelProps> = ({
  basketId,
  editingStrategy,
  onDone,
  onCancel,
  onDirtyChange,
}) => {
  const wl = isWhiteLabel();
  const focusRing = wl ? 'focus:ring-zebu-500' : 'focus:ring-blue-500';
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [configOpen, setConfigOpen] = useState(true);
  const [lotNudge, setLotNudge] = useState(false);
  const [dupLegNudge, setDupLegNudge] = useState(false);
  const { showSuccess, showError } = useToast();

  const {
    strategyName,
    setStrategyName,
    strategyIndex,
    setStrategyIndex,
    legs,
    setLegs,
    strategyConfig,
    setStrategyConfig,
    baselineSignature,
  } = useStrategyForm({ basketId, editingStrategy, onClose: onCancel });

  const { actions, updatePosition } = usePositionManagement({ legs, setLegs, showError });
  const validation = useStrategyValidation();

  const { handleSubmit, isSubmitting } = useStrategySubmission({
    basketId,
    strategyName,
    strategyIndex,
    legs,
    strategyConfig,
    editingStrategy,
    onSubmit: onDone,
    // Post-save closing is delegated to onDone so the caller decides what happens
    // (a dialog closes itself; an inline row-expand stays open). Without this,
    // the hook's own onClose would collapse the inline editor on every save.
    onClose: () => {},
    showError,
    showSuccess,
    setValidationErrors,
    validation,
  });

  // Clear stale validation errors as soon as the user edits anything material.
  useEffect(() => {
    setValidationErrors((prev) => (prev.length === 0 ? prev : []));
  }, [legs, strategyName, strategyConfig]);

  // ── Unsaved-edit tracking (guards accidental collapse / refresh) ──────────
  // Value-based, not touched-flag: a net-zero round-trip edit (e.g. a leg
  // toggled CE → PE → CE) returns the form to its loaded state, so the live
  // signature matches the baseline and `dirty` is false — no false "discard?"
  // prompt. `baselineSignature` is captured from the strategy as loaded.
  const liveSignature = useMemo(
    () => computeStrategySignature(strategyName, strategyIndex, strategyConfig, legs),
    [strategyName, strategyIndex, strategyConfig, legs],
  );
  const dirty = liveSignature !== baselineSignature;

  // Leg-aware Combined-Premium % MTM cap: relax >100 only where the leg mix can
  // reach it (SL needs a SELL leg, TP needs a BUY leg). See utils `legExposure`.
  const { hasSellLeg, hasBuyLeg } = useMemo(() => legExposure(legs), [legs]);

  // Surface dirty state to the host row, which gates collapse / row-switch.
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  // Native guard for browser refresh / tab close while edits are pending.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // DTE-mode eligibility: weekly expiry + non-MONTHLY_ONLY underlying.
  // Mirrors the backend validator (strategy_manager._validate_schedule_fields)
  // so we never ship a DTE+next_weekly / DTE+monthly / DTE+BANKNIFTY combo to
  // the API. Returns true when the supplied (expiryType, index) pair is DTE-OK.
  const isDteEligible = (expiryType: string | undefined, index: string | undefined) =>
    expiryType === EXPIRY_TYPE_CONST.WEEKLY &&
    !!index &&
    !MONTHLY_ONLY_UNDERLYINGS.includes(index);

  const handleConfigUpdate = (updates: Partial<StrategyConfig>) => {
    setStrategyConfig((prev) => {
      const next = { ...prev, ...updates };
      // Auto-flip DTE mode back to WEEKDAY when the strategy becomes
      // DTE-ineligible (expiry not weekly). Mirrors StrategyRowView's
      // effectiveMode fallback so the form can't silently submit an
      // invalid combo. entryDtes is preserved so flipping back is lossless.
      if (
        next.entrySelectionMode === ENTRY_SELECTION_MODE.DTE &&
        !isDteEligible(next.expiryType, strategyIndex)
      ) {
        next.entrySelectionMode = ENTRY_SELECTION_MODE.WEEKDAY;
      }
      // Auto-clear leg-level WAT continueOvernight when the strategy becomes
      // non-overnight (e.g. user picked BTST, checked Overnight, then switched
      // to SAME_DAY). LegMatrix hides the checkbox in this state but doesn't
      // clear the underlying leg state, so without this auto-clear a stale
      // true would slip through transformation. Mirrors the ORB
      // orbContinueOvernight auto-clear effect in StrategyConfiguration.
      const nextIsOvernight = (
        next.tradingType === 'POSITIONAL'
        || (next.tradingType === 'INTRADAY'
            && next.intradayExitMode === 'NEXT_DAY_BTST')
      );
      if (!nextIsOvernight) {
        setLegs((prevLegs) => prevLegs.map((leg) => (
          leg.waitAndTrade?.continueOvernight
            ? { ...leg, waitAndTrade: { ...leg.waitAndTrade, continueOvernight: false } }
            : leg
        )));
      }
      return next;
    });
  };

  // Same guard for underlying changes (strategyIndex is separate state).
  const handleIndexChange = (next: string) => {
    setStrategyIndex(next);
    if (
      strategyConfig.entrySelectionMode === ENTRY_SELECTION_MODE.DTE &&
      !isDteEligible(strategyConfig.expiryType, next)
    ) {
      setStrategyConfig((prev) => ({
        ...prev,
        entrySelectionMode: ENTRY_SELECTION_MODE.WEEKDAY,
      }));
    }
  };

  const isOvernight =
    strategyConfig.tradingType === 'POSITIONAL' || strategyConfig.intradayExitMode === 'NEXT_DAY_BTST';

  const showBtstNotice =
    strategyConfig.tradingType === TRADING_TYPE.INTRADAY &&
    strategyConfig.intradayExitMode === INTRADAY_EXIT_MODE.NEXT_DAY_BTST &&
    !strategyConfig.expiryDayRollover &&
    strategyConfig.expiryType !== EXPIRY_TYPE_CONST.NEXT_WEEKLY &&
    strategyConfig.expiryType !== EXPIRY_TYPE_CONST.NEXT_MONTHLY;

  // LegMatrix is index-based; bridge to the id-based position actions
  // (which run autoCorrectPositionInterdependencies on every update).
  const onUpdateLeg = (i: number, patch: Partial<Leg>) => {
    const leg = legs[i];
    if (leg) updatePosition(leg.id, patch);
  };
  const onRemoveLeg = (i: number) => {
    const leg = legs[i];
    if (leg) actions.remove(leg.id);
  };
  const onCopyLeg = (i: number) => {
    const leg = legs[i];
    if (leg) actions.copy(leg.id);
  };
  const onAddHedge = (i: number) => {
    const leg = legs[i];
    if (leg) actions.addHedge(leg.id);
  };

  // Both soft nudges compare MAIN legs only — a protective hedge is expected to
  // carry a different lot count than its main (e.g. 10 main / 5 hedge) and to
  // duplicate another main's hedge (two mains each with an identical BUY hedge),
  // so including hedges would fire these nudges on legitimate hedged structures.
  const mainLegs = useMemo(() => legs.filter((l) => !l.isHedge), [legs]);

  // Soft nudge (not a hard block) when legs carry different lot sizes — e.g.
  // CE 10 / PE 8. Common for intentional ratio spreads, but more often a typo.
  const lotsMismatch = mainLegs.length > 1 && new Set(mainLegs.map((l) => l.totalLots)).size > 1;

  // Soft nudge when two+ legs share the same leg type (option type + action),
  // the same strike-selection method, AND the same selection value — a
  // fully-redundant/duplicate leg.
  const duplicateLegGroups = useMemo(() => findDuplicateLegTypeGroups(mainLegs), [mainLegs]);

  // Save gates run in sequence: duplicate-leg nudge → lot-mismatch nudge → submit.
  // Each ConfirmDialog auto-closes on confirm, so confirming one advances to the next.
  const proceedAfterDupCheck = () => {
    if (lotsMismatch) setLotNudge(true);
    else handleSubmit();
  };
  const handleSaveClick = () => {
    if (duplicateLegGroups.length > 0) setDupLegNudge(true);
    else proceedAfterDupCheck();
  };

  return (
    <div className="space-y-4 pb-2">
      {/* strategy name */}
      <div className="px-4 pt-4 flex items-center gap-3">
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap shrink-0">Strategy Name</label>
        <input
          type="text"
          value={strategyName}
          onChange={(e) => setStrategyName(e.target.value)}
          placeholder="e.g. Short Straddle 9:20"
          className={`flex-1 max-w-md text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 ${focusRing}`}
        />
      </div>

      <WizardErrorSummary errors={validationErrors} onDismiss={() => setValidationErrors([])} />

      {/* Part 1 — Contract: index / expiry / product / Move-SL-to-Cost (two rows, no heading). Always visible, above the legs. */}
      <div className="px-4">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 px-3 py-3">
          <GlobalIndexSelector
            value={strategyIndex}
            onChange={handleIndexChange}
            expiryType={strategyConfig.expiryType}
            onExpiryTypeChange={(expiryType) => handleConfigUpdate({ expiryType })}
            productType={strategyConfig.productType}
            onProductTypeChange={(productType) => handleConfigUpdate({ productType })}
            tradingType={strategyConfig.tradingType}
            intradayExitMode={strategyConfig.intradayExitMode}
          />
        </div>
      </div>

      {/* legs */}
      <div className="px-4">
        {legs.length > 0 ? (
          <LegMatrix
            legs={legs}
            onUpdateLeg={onUpdateLeg}
            onAddLeg={actions.add}
            onAddHedge={onAddHedge}
            onRemoveLeg={onRemoveLeg}
            onCopyLeg={onCopyLeg}
            isOrbEnabled={strategyConfig.rangeBreakout}
            isOvernight={isOvernight}
          />
        ) : (
          <button
            type="button"
            onClick={actions.add}
            className={`text-sm font-medium ${wl ? 'text-zebu-600 hover:text-zebu-700' : 'text-blue-600 hover:text-blue-700'}`}
          >
            + Add your first leg
          </button>
        )}
      </div>

      {/* Part 2 — Schedule & Risk: timing / trading mode / range breakout / strategy TP-SL. Collapsible, below the legs. */}
      <div className="px-4">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40">
          <button
            type="button"
            onClick={() => setConfigOpen((o) => !o)}
            className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/30 rounded-lg transition-colors"
          >
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Schedule &amp; Risk
            </span>
            <span className="flex items-center gap-2 text-xs font-normal normal-case text-gray-400 dark:text-gray-500">
              {!configOpen && (
                <span>
                  {strategyConfig.tradingType} · {strategyConfig.entryTimeHour}:{strategyConfig.entryTimeMinute}–{strategyConfig.exitTimeHour}:{strategyConfig.exitTimeMinute}
                </span>
              )}
              {configOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </span>
          </button>
          {configOpen && (
            <div className="px-3 pb-3 pt-3 space-y-4 border-t border-gray-100 dark:border-gray-700/60">
              {showBtstNotice && (
                <div className="flex items-start gap-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 text-xs text-blue-800 dark:text-blue-200">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Entries will be skipped on the underlying's expiry day. STBT/BTST positions need a next trading day
                    to exit into, so this strategy will not enter on expiry days. To run STBT/BTST on the expiry day,
                    enable <strong>Continue on expiry days (next contract)</strong> below — the entry will use next week's contract.
                  </span>
                </div>
              )}
              <StrategyConfiguration
                config={strategyConfig}
                onUpdate={handleConfigUpdate}
                slConfiguredOnAnyLeg={legs.some((leg) => leg.stopLoss?.enabled)}
                hasSellLeg={hasSellLeg}
                hasBuyLeg={hasBuyLeg}
              />
            </div>
          )}
        </div>
      </div>

      {/* footer */}
      <div className="sticky bottom-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={isSubmitting} className="min-w-[80px]">
          Cancel
        </Button>
        <Button
          onClick={handleSaveClick}
          disabled={isSubmitting || legs.length === 0}
          className={`${wl ? 'bg-zebu-600 hover:bg-zebu-700' : 'bg-blue-600 hover:bg-blue-700'} text-white min-w-[120px]`}
        >
          {isSubmitting
            ? editingStrategy
              ? 'Updating…'
              : 'Creating…'
            : editingStrategy
              ? 'Update Strategy'
              : 'Create Strategy'}
        </Button>
      </div>

      <ConfirmDialog
        isOpen={lotNudge}
        onClose={() => setLotNudge(false)}
        onConfirm={handleSubmit}
        title="Leg quantities don't match"
        confirmText="Save anyway"
        cancelText="Review quantities"
        variant="info"
        message={
          <div>
            <p className="mb-2">These legs have different lot sizes — usually they should match:</p>
            <ul className="space-y-0.5">
              {legs.map((l, i) => (
                <li key={l.id} className="tabular-nums">
                  Leg {i + 1} — {l.optionType}/{l.actionType}:{' '}
                  <span className="font-semibold text-gray-800 dark:text-gray-100">
                    {l.totalLots} lot{l.totalLots !== 1 ? 's' : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        }
      />

      <ConfirmDialog
        isOpen={dupLegNudge}
        onClose={() => setDupLegNudge(false)}
        onConfirm={proceedAfterDupCheck}
        title="Duplicate leg configuration"
        confirmText="Save anyway"
        cancelText="Review legs"
        variant="info"
        size="lg"
        message={
          <div>
            <p className="mb-2">
              These legs share the same type, strike-selection method, and value — usually only one is needed:
            </p>
            <ul className="space-y-0.5">
              {duplicateLegGroups.map((g) => (
                <li
                  // Leg-number sets are disjoint across groups, so this is a
                  // guaranteed-unique stable key (unlike a valueLabel composite,
                  // which can collide across empty-slot permutations).
                  key={g.legNumbers.join('-')}
                  className="tabular-nums"
                >
                  <span className="font-semibold text-gray-800 dark:text-gray-100">
                    {g.actionType} {g.optionType} ·{' '}
                    {SELECTION_METHOD_OPTIONS.find((o) => o.value === g.selectionMethod)?.label ?? g.selectionMethod}
                    {g.valueLabel ? ` · ${g.valueLabel}` : ''}
                  </span>
                  {' '}— Legs {g.legNumbers.join(', ')}
                </li>
              ))}
            </ul>
          </div>
        }
      />
    </div>
  );
};

export default StrategyEditorPanel;
